<template>
  <div>
    <fixed-label-filter v-if="isTemplate" :label="$t('bill.analysis_cost_alarm')">
      <base-select
        v-model="alarmId"
        resource="bill_analysis_alarms"
        isDefaultSelect
        :disabled="!isTemplateEdit"
        :select-props="{ placeholder: $t('common.tips.select', [$t('bill.analysis_cost_alarm')]) }"
        :params="alarmParams"
        @update:item="handleSelectAlarm" />
    </fixed-label-filter>
    <page-list
      :list="list"
      :columns="templateListColumns || columns"
      :single-actions="singleActions"
      :group-actions="groupActions"
      :export-data-options="exportDataOptions"
      :showSearchbox="showSearchbox"
      :show-group-actions="showGroupActions"
      :show-single-actions="!isTemplate"
      :show-page="!isTemplate" />
  </div>
</template>

<script>
import * as R from 'ramda'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import {
  getNameFilter,
  // getCreatedAtFilter,
} from '@/utils/common/tableFilter'
import { numerify } from '@/filters'
import { currencyUnitMap } from '@/constants/currency'
import ResTemplateListMixin from '@/mixins/resTemplateList'
import GlobalSearchMixin from '@/mixins/globalSearch'
import { getGroupList } from '../utils'

export default {
  name: 'AnalysisAlarmRecordList',
  mixins: [WindowsMixin, ListMixin, ResTemplateListMixin, GlobalSearchMixin],
  props: {
    id: String,
    data: Object,
    getParams: Object,
    isTemplate: Boolean,
    isTemplateEdit: Boolean,
  },
  data () {
    return {
      alarmId: this.templateParams?.alarmId || undefined,
      alarmData: {},
      list: this.$list.createList(this, {
        id: this.id,
        resource: 'bill_analysis_alarm_records',
        apiVersion: 'v1',
        getParams: this.getParam,
        filterOptions: {
          name: getNameFilter({
            label: this.$t('bill.alarm_date'),
          }),
          // created_at: getCreatedAtFilter(),
        },
        isTemplate: this.isTemplate,
        templateLimit: this.templateLimit,
      }),
      groupActions: [
        {
          label: this.$t('common.delete'),
          permission: 'bill_analysis_reports_delete',
          action: () => {
            const data = this.list.selectedItems
            this.createDialog('DeleteResDialog', {
              vm: this,
              data,
              columns: this.columns,
              title: this.$t('common.delete'),
              name: this.$t('bill.analysis_report'),
              onManager: this.onManager,
            })
          },
          meta: () => {
            return {
              validate: this.list.selectedItems.length > 0,
            }
          },
        },
      ],
    }
  },
  computed: {
    columns () {
      let groupColumns = []
      let amountColumns = []
      let currencySign = ''
      const { query_attributes, currency, time_range } = this.data || this.alarmData || {}
      if (query_attributes) {
        groupColumns = getGroupList(query_attributes).map(item => item.label).map((label, index) => {
          return {
            field: `group_${index}`,
            title: label,
            formatter: ({ row }) => {
              return row.alarm_groups[index]
            },
          }
        })
        if (currencyUnitMap[currency]) {
          currencySign = currencyUnitMap[currency].sign || ''
        }
        const timeRange = (time_range || '').toLowerCase()
        amountColumns = [
          {
            field: 'amount',
            title: this.$t('bill.daily_cost'),
            sortable: true,
            formatter: ({ row }) => {
              return `${currencySign} ${numerify(row.amount, '0,0.00')}`
            },
          },
          {
            field: 'prev_amount',
            title: this.$t('bill.last_month_daily_cost'),
            sortable: true,
            formatter: ({ row }) => {
              return `${currencySign} ${numerify(row.prev_amount, '0,0.00')}`
            },
          },
        ]
        if (timeRange === 'dod') {
          amountColumns[1].title = this.$t('bill.last_daily_cost')
        }
        if (timeRange === 'mom pc') {
          amountColumns[0].title = this.$t('bill.monthly_cost')
          amountColumns[1].title = this.$t('bill.last_year_monthly_cost')
        }
        if (timeRange === 'mom') {
          amountColumns[0].title = this.$t('bill.monthly_cost')
          amountColumns[1].title = this.$t('bill.last_month_monthly_cost')
        }
      }
      return [
        {
          field: 'date_info',
          title: this.$t('bill.alarm_date'),
          sortable: true,
        },
        ...groupColumns,
        ...amountColumns,
        {
          field: 'threshold',
          title: this.$t('bill.budget_alert_threshold'),
          formatter: ({ row }) => {
            const trendText = this.$te(`bill.trend_${(row.trend || '').toLowerCase()}`) ? this.$t(`bill.trend_${(row.trend || '').toLowerCase()}`) : row.trend
            if (row.alarm_abs_threshold) {
              return `${trendText} ${currencySign} ${numerify(row.alarm_abs_threshold, '0,0.00')}`
            }
            if (row.alarm_rel_threshold) {
              return `${trendText} ${row.alarm_rel_threshold} %`
            }
            return '-'
          },
        },
        {
          field: 'aoc',
          title: this.$t('bill.trend_percentage'),
          sortable: true,
          formatter: ({ row }) => {
            const { aoc = 0, amount = 0, prev_amount = 0, alarm_rel_threshold = 0 } = row
            let trendType = 'absolute'
            if (alarm_rel_threshold) {
              trendType = 'relative'
            }
            if (aoc === 0) {
              return this.$t('bill.no_change')
            }
            return (amount - prev_amount > 0 ? this.$t('bill.trend_asc') : this.$t('bill.trend_desc')) + ' ' + (trendType === 'absolute' ? (currencySign || '') + numerify(aoc, '0,0.00') : aoc.toFixed(2) + '%')
          },
        },
      ]
    },
    alarmParams () {
      const ret = {
        scope: this.$store.getters.scope,
        details: true,
      }
      return ret
    },
    exportDataOptions () {
      return {
        downloadType: 'local',
        title: this.$t('bill.alert_record'),
        items: this.columns,
      }
    },
  },
  watch: {
    columns: {
      handler (newVal) {
        this.$emit('update:columns', newVal)
      },
      deep: true,
    },
    alarmId: {
      handler (newVal) {
        this.list.reset(true)
        this.fetchData()
      },
    },
  },
  created () {
    this.fetchData()
  },
  methods: {
    getParam () {
      const ret = R.is(Function, this.getParams) ? this.getParams() : this.getParams
      if (this.alarmId || this.templateParams?.alarmId) {
        ret.bill_analysis_alarm_id = this.alarmId || this.templateParams?.alarmId
      }
      ret.order_by = 'date_info'
      ret.order = 'desc'
      return ret
    },
    handleSelectAlarm (item) {
      this.alarmData = item
    },
    fetchData () {
      this.list.fetchData()
    },
    handleOpenSidepage (row) {
      this.sidePageTriggerHandle(this, 'BillAnalysisAlarmSidePage', {
        id: row.id,
        resource: 'bill_analysis_alarms',
        getParams: this.getParam,
      }, {
        list: this.list,
      })
    },
  },
}
</script>
